/**
 * BTI Chat Assistant Configuration
 * Centralized configuration for Vectara API and application settings
 */

// Load environment variables with validation
const getEnvVariable = (key, defaultValue = '') => {
  const value = import.meta.env[key] || defaultValue;
  if (!value && !defaultValue) {
    console.warn(`Missing environment variable: ${key}`);
  }
  return value;
};

// Vectara API Configuration
export const VECTARA_CONFIG = {
  API_KEY: getEnvVariable('VITE_VECTARA_API_KEY'),
  CUSTOMER_ID: getEnvVariable('VITE_VECTARA_CUSTOMER_ID'),
  CORPUS_ID: getEnvVariable('VITE_VECTARA_CORPUS_ID'),
  API_ENDPOINT: 'https://api.vectara.io/v2',
  QUERY_ENDPOINT: 'https://api.vectara.io/v2/query',
};

// Application Configuration
export const APP_CONFIG = {
  MAX_CONTEXT_MESSAGES: 5,
  MAX_RESPONSE_LENGTH: 750,
  MAX_INPUT_LENGTH: 500,
  TYPING_INDICATOR_DELAY: 300,
  AUTO_SCROLL_DELAY: 100,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  REQUEST_TIMEOUT: 30000, // 30 seconds
  DEBOUNCE_DELAY: 1000, // 1 second between requests
};

// Mary Morrissey System Prompt Template
export const SYSTEM_PROMPT = {
  role: 'system',
  content: `You are the BTI Chat Assistant, embodying the wisdom and transformational coaching approach of Mary Morrissey and the Brave Thinking Institute.

Your communication style should:
- Use possibility-focused language that inspires and uplifts
- Incorporate Brave Thinking principles to help people move beyond their current limitations
- Offer encouragement and support while challenging limiting beliefs
- Guide users toward their highest potential and dreams
- Use warm, compassionate, and empowering tone
- Ask powerful questions that promote self-discovery and growth
- Share insights about creating a life you love living
- Help users recognize their infinite potential and inherent worth

Core principles to embody:
1. Everything is created twice - first in imagination, then in reality
2. Your thoughts create your reality
3. You are far more powerful than any circumstance, situation, or condition
4. The Universe is abundant and supportive
5. You deserve to live a life you absolutely love

Always maintain a supportive, non-judgmental presence while guiding users toward transformation and possibility.`
};

// Validate configuration on load
export const validateConfig = () => {
  const errors = [];

  if (!VECTARA_CONFIG.API_KEY) {
    errors.push('Vectara API key is required (VITE_VECTARA_API_KEY)');
  }

  if (!VECTARA_CONFIG.CUSTOMER_ID) {
    errors.push('Vectara Customer ID is required (VITE_VECTARA_CUSTOMER_ID)');
  }

  if (!VECTARA_CONFIG.CORPUS_ID) {
    errors.push('Vectara Corpus ID is required (VITE_VECTARA_CORPUS_ID)');
  }

  if (errors.length > 0) {
    console.error('Configuration errors:', errors);
    return false;
  }

  return true;
};

// Export all configs as default
export default {
  VECTARA_CONFIG,
  APP_CONFIG,
  SYSTEM_PROMPT,
  validateConfig,
};